/**
 * Public API for the validators feature
 * This is the main entry point for consuming the validation system
 */

// Core types
export type {
  ValidationResult,
  Validator,
  ValidatorOptions,
  ValidatorConfig,
  FieldValidationConfig,
  ValidationState,
  FormValidationConfig,
  FormValidationHookResult
} from './types'

// Validator registry
export { 
  VALIDATORS, 
  getValidator, 
  getValidatorNames 
} from './registry'

// React hooks
export { useFormValidation } from './hooks/useFormValidation'
export { useFieldValidation } from './hooks/useFieldValidation'

// UI Components
export { ValidatedInput } from './components/ValidatedInput'
export { ValidatedSelect } from './components/ValidatedSelect'
export { ValidatedTextarea } from './components/ValidatedTextarea'
export { ValidationMessage } from './components/ValidationMessage'

// Utility functions for manual validation
import type { ValidationResult } from './types'
import { VALIDATORS } from './registry'

export const validateValue = (
  value: string, 
  validatorName: string, 
  options?: any
): ValidationResult => {
  const validator = VALIDATORS[validatorName]
  if (!validator) {
    return { isValid: true }
  }
  return validator.validate(value, options)
}

export const formatValue = (
  value: string,
  validatorName: string
): string => {
  const validator = VALIDATORS[validatorName]
  if (!validator?.formatInput) {
    return value
  }
  return validator.formatInput(value)
}

// Helper to create validation config from field mappings
import type { FormValidationConfig } from './types'

export const createValidationConfig = (
  fieldMappings: Array<{
    formFieldName: string
    formFieldType?: string
    isRequired: boolean
    validationRules?: {
      validators?: Array<{
        name: string
        options?: any
        customMessage?: string
      }>
      formatOnInput?: boolean
      validateOnChange?: boolean
      validateOnBlur?: boolean
      showSuccessIcon?: boolean
    }
  }>
): FormValidationConfig => {
  const config: FormValidationConfig = {}
  
  fieldMappings.forEach(mapping => {
    let validators = mapping.validationRules?.validators || []
    
    // Debug logging for contactOwnershipPercentage
    if (mapping.formFieldName === 'contactOwnershipPercentage') {
      console.log('🔧 Processing contactOwnershipPercentage:')
      console.log('   Field type:', mapping.formFieldType)
      console.log('   Validation rules from DB:', mapping.validationRules)
      console.log('   Initial validators:', validators)
    }
    
    // Add default validators based on field type if none are specified
    if (validators.length === 0) {
      switch (mapping.formFieldType) {
        case 'email':
          validators = [{ name: 'email' }]
          break
        case 'phone':
          validators = [{ name: 'phoneNumber' }]
          break
        case 'percentage':
          // For percentage fields, add wholeNumberOnly validator
          validators = [{ name: 'wholeNumberOnly' }]
          break
        case 'currency':
          validators = [{ name: 'currency' }]
          break
        case 'url':
          validators = [{ name: 'url' }]
          break
      }
    }
    
    // Ensure all validators are in the correct object format
    validators = validators.map(validator => {
      if (typeof validator === 'string') {
        return { name: validator }
      }
      return validator
    })
    
    // Debug logging for contactOwnershipPercentage after processing
    if (mapping.formFieldName === 'contactOwnershipPercentage') {
      console.log('   Final validators after processing:', validators)
      
      // Test the wholeNumberOnly validator directly
      const wholeNumberValidator = VALIDATORS.wholeNumberOnly
      if (wholeNumberValidator) {
        const testResult1 = wholeNumberValidator.validate('100')
        const testResult2 = wholeNumberValidator.validate('100.00')
        console.log('   🧪 Testing wholeNumberOnly validator:')
        console.log('     "100" result:', testResult1)
        console.log('     "100.00" result:', testResult2)
      }
    }
    
    config[mapping.formFieldName] = {
      required: mapping.isRequired,
      validators: validators,
      formatOnInput: mapping.validationRules?.formatOnInput,
      validateOnChange: mapping.validationRules?.validateOnChange,
      validateOnBlur: mapping.validationRules?.validateOnBlur,
      showSuccessIcon: mapping.validationRules?.showSuccessIcon
    }
  })
  
  return config
}
